import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Send, Mail, TestTube, History, AlertCircle, CheckCircle2, Loader2, Users, Link as LinkIcon, UsersRound } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminNewsletterPage = () => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const token = localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchLogs();
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGroups(data.filter(g => g.is_active && g.active_contacts > 0));
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const toggleGroupSelection = (groupId) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const getTotalRecipients = () => {
    return groups.filter(g => selectedGroupIds.includes(g.id)).reduce((sum, g) => sum + g.active_contacts, 0);
  };

  const plainTextToHtml = (text) => {
    return text.split('\n').filter(l => l.trim()).map(l => `<p>${l}</p>`).join('');
  };

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleSendTest = async () => {
    if (!subject.trim() || !message.trim() || !testEmail.trim()) {
      toast.error('Bitte füllen Sie Betreff, Nachricht und Test-E-Mail aus');
      return;
    }

    setSendingTest(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          subject,
          content_html: plainTextToHtml(message),
          test_mode: true,
          test_email: testEmail
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Test-Newsletter an ${testEmail} gesendet`);
      } else {
        toast.error(data.detail || 'Fehler beim Senden');
      }
    } catch (error) {
      toast.error('Fehler beim Senden des Test-Newsletters');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendAll = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Bitte füllen Sie Betreff und Nachricht aus');
      return;
    }

    setSending(true);
    setShowConfirm(false);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          subject,
          content_html: plainTextToHtml(message),
          group_ids: selectedGroupIds
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setSubject('');
        setMessage('');
        setSelectedGroupIds([]);
        fetchLogs();
      } else {
        toast.error(data.detail || 'Fehler beim Senden');
      }
    } catch (error) {
      toast.error('Fehler beim Senden des Newsletters');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="text-primary" size={28} />
            <h1 className="text-2xl font-['Oswald'] font-bold tracking-wider uppercase">
              Newsletter
            </h1>
          </div>
          <p className="text-muted-foreground">
            Senden Sie einen Newsletter an alle Mitglieder
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Newsletter Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-border p-6">
              <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <Send size={18} />
                Newsletter verfassen
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-2 uppercase tracking-wider">
                    Betreff *
                  </label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Newsletter Betreff..."
                    className="bg-background border-border"
                    data-testid="newsletter-subject"
                  />
                </div>

                <div>
                  <label className="block text-sm text-muted-foreground mb-2 uppercase tracking-wider">
                    Nachricht *
                  </label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Ihre Nachricht an die Mitglieder..."
                    className="bg-background border-border min-h-[250px]"
                    data-testid="newsletter-message"
                  />
                </div>

                {/* Test Email Section */}
                <div className="pt-4 border-t border-border">
                  <label className="block text-sm text-muted-foreground mb-2 uppercase tracking-wider">
                    Test-E-Mail (optional)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                      className="bg-background border-border flex-1"
                      data-testid="newsletter-test-email"
                    />
                    <Button
                      onClick={handleSendTest}
                      disabled={sendingTest || !subject.trim() || !message.trim() || !testEmail.trim()}
                      variant="outline"
                      className="gap-2"
                      data-testid="newsletter-send-test-btn"
                    >
                      {sendingTest ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <TestTube size={16} />
                      )}
                      Test senden
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Senden Sie zuerst einen Test, um die Nachricht zu überprüfen
                  </p>
                </div>

                {/* Empfänger-Auswahl - Dialog-Style */}
                <div className="pt-4">
                  <div className="border border-border bg-background" data-testid="newsletter-groups-section">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/20">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-muted-foreground">Senden an:</span>
                      </div>
                      <Link to="/admin/newsletter-groups" className="text-xs text-primary hover:underline">Gruppen verwalten</Link>
                    </div>

                    {/* Group List */}
                    <div className="max-h-[300px] overflow-y-auto divide-y divide-border/30">
                      {groups.length > 0 ? groups.map(group => (
                        <label
                          key={group.id}
                          className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors ${
                            selectedGroupIds.includes(group.id)
                              ? 'bg-primary/5'
                              : 'hover:bg-muted/30'
                          }`}
                          data-testid={`newsletter-group-option-${group.id}`}
                        >
                          <Checkbox
                            checked={selectedGroupIds.includes(group.id)}
                            onCheckedChange={() => toggleGroupSelection(group.id)}
                            className="h-5 w-5"
                            data-testid={`newsletter-group-checkbox-${group.id}`}
                          />
                          <span className={`flex-1 text-sm ${selectedGroupIds.includes(group.id) ? 'font-semibold text-foreground' : 'text-foreground/80'}`}>
                            {group.name}
                          </span>
                          {group.is_system && (
                            <span className="text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 uppercase font-medium mr-1">Auto</span>
                          )}
                          <span className="text-sm text-muted-foreground tabular-nums min-w-[80px] text-right">
                            {group.active_contacts.toLocaleString('de-CH')} Kontakte
                          </span>
                        </label>
                      )) : (
                        <p className="px-5 py-4 text-sm text-muted-foreground">Keine Gruppen vorhanden</p>
                      )}
                    </div>

                    {/* Footer - Recipient Count */}
                    <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/20">
                      <span className="text-sm font-medium text-muted-foreground">Empfänger:</span>
                      <span className="text-sm font-semibold tabular-nums" data-testid="newsletter-recipient-count">
                        {selectedGroupIds.length > 0 ? (
                          <>
                            ~{getTotalRecipients().toLocaleString('de-CH')}
                            <span className="text-xs text-muted-foreground font-normal ml-1">(Duplikate werden entfernt)</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground font-normal">Bitte Gruppen wählen</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Send Button */}
                  <div className="mt-4 flex gap-3">
                    <Button
                      onClick={showConfirm ? handleSendAll : () => setShowConfirm(true)}
                      disabled={sending || !subject.trim() || !message.trim() || selectedGroupIds.length === 0}
                      className="flex-1 bg-primary hover:bg-primary/90 text-white gap-2 h-12 text-sm uppercase tracking-wider"
                      data-testid={showConfirm ? "newsletter-confirm-send-btn" : "newsletter-send-all-btn"}
                    >
                      {sending ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : showConfirm ? (
                        <CheckCircle2 size={18} />
                      ) : (
                        <Send size={18} />
                      )}
                      {sending
                        ? 'Wird gesendet...'
                        : showConfirm
                          ? `Jetzt an ~${getTotalRecipients().toLocaleString('de-CH')} senden`
                          : selectedGroupIds.length === 0
                            ? 'Bitte Empfänger wählen'
                            : 'Senden'
                      }
                    </Button>
                    {showConfirm && (
                      <Button
                        onClick={() => setShowConfirm(false)}
                        variant="outline"
                        disabled={sending}
                        className="h-12 px-6 uppercase text-xs tracking-wider"
                        data-testid="newsletter-cancel-btn"
                      >
                        Abbrechen
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Send History */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <History size={18} />
                Verlauf
              </h2>

              {loadingLogs ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-muted-foreground" />
                </div>
              ) : logs.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  Noch keine Newsletter gesendet
                </p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {logs.map((log, idx) => (
                    <div
                      key={log.id || idx}
                      className="p-3 bg-background border border-border text-sm"
                      data-testid={`newsletter-log-${idx}`}
                    >
                      <p className="font-medium text-white truncate" title={log.subject}>
                        {log.subject}
                      </p>
                      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                        <span>{formatDate(log.sent_at || log.created_at)}</span>
                        <span className="flex items-center gap-1">
                          <Mail size={12} />
                          {log.recipients || log.sent_count || 0} gesendet
                        </span>
                      </div>
                      {(log.failed || log.failed_count || 0) > 0 && (
                        <p className="text-xs text-red-400 mt-1">
                          {log.failed || log.failed_count} fehlgeschlagen
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminNewsletterPage;
